import { Fragment, useEffect, useRef, useState, type RefObject } from "react";
import {
  ArrowLeft,
  CalendarHeart,
  Download,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { DateField } from "../../../ui/DateField";
import { StatsLoadingCards } from "../../../ui/StatsLoadingCards";
import { sponsorApi } from "../lib/api";

type Counters = {
  clicks: number;
  conversions: number;
  unique_conversions: number;
  verified_starts: number;
  verified_unbilled_starts: number;
  lost: number;
  suspicious: number;
};

type SponsorResource = {
  id: number;
  name: string;
  public_name: string | null;
  status: string;
  category: string | null;
  type: string;
  price_per_action: string;
  driver_rate: string;
  clicks: number;
  conversions: number;
  verified_starts: number;
  verified_unbilled_starts: number;
  subscribers: number;
  lost: number;
  spent: string;
};

type Stats = {
  snapshot_generated_at: string;
  sponsor_started_at?: string;
  balance: {
    balance: string;
    spent_total: string;
    topup_total: string;
  };
  today: Counters;
  all_time: Counters;
  filtered: Counters;
  days: Array<Counters & { date: string }>;
  resources: SponsorResource[];
  topups: Array<{
    id: number;
    method: string;
    amount: string;
    status: string;
    created_at: string;
    completed_at: string | null;
  }>;
  spends: Array<{
    id: number;
    amount: string;
    kind: string;
    memo: string | null;
    created_at: string;
  }>;
};

type Props = {
  resourceId?: number;
  resourceName?: string;
  onBack?: () => void;
};

// Keep background polling aligned with the API's five-minute response cache.
const STATS_REFRESH_MS = 300_000;
const SPONSOR_STATS_LOADING_ROWS = [3] as const;

export function SponsorStats({ resourceId, resourceName, onBack }: Props) {
  const [initialDate] = useState(todayIso);
  const [stats, setStats] = useState<Stats | null>(null);
  const [dateFrom, setDateFrom] = useState(initialDate);
  const [dateTo, setDateTo] = useState(initialDate);
  const [appliedFilters, setAppliedFilters] = useState({
    dateFrom: initialDate,
    dateTo: initialDate,
  });
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const statsRequestId = useRef(0);
  const statsRequestInFlight = useRef<Promise<boolean> | null>(null);
  const latestSnapshotByKey = useRef(new Map<string, number>());
  const statsHeadingRef = useRef<HTMLHeadingElement>(null);
  const restoreStatsHeadingFocus = useRef(false);

  async function load(
    nextDateFrom = dateFrom,
    nextDateTo = dateTo,
    options: {
      showLoading?: boolean;
      silent?: boolean;
      focusHeading?: boolean;
      refresh?: boolean;
    } = {},
  ) {
    const showLoading = options.showLoading ?? true;
    const silent = options.silent ?? false;
    const focusHeading = options.focusHeading ?? false;
    const refresh = options.refresh ?? false;
    const isBackgroundRefresh = silent && !showLoading;
    const pendingRequest = statsRequestInFlight.current;
    if (isBackgroundRefresh && pendingRequest) return false;

    const requestId = ++statsRequestId.current;
    if (showLoading) {
      if (focusHeading) restoreStatsHeadingFocus.current = true;
      setLoading(true);
    }
    if (!silent) setError(null);
    if (pendingRequest) {
      await pendingRequest;
      if (requestId !== statsRequestId.current) return false;
    }

    const request = (async () => {
      try {
        const r = await sponsorApi.getStats({
          dateFrom: nextDateFrom,
          dateTo: nextDateTo,
          resourceId,
          ...(refresh ? { refresh: true } : {}),
        });
        if (requestId !== statsRequestId.current) return false;
        if (r.error || !r.data) {
          if (!silent) {
            setError("не удалось загрузить статистику");
            setStats(null);
          }
          return false;
        }
        const nextStats = r.data as Stats;
        const snapshotKey = [resourceId ?? "", nextDateFrom, nextDateTo].join(
          "|",
        );
        if (
          !acceptSnapshot(
            latestSnapshotByKey.current,
            snapshotKey,
            nextStats.snapshot_generated_at,
          )
        ) {
          setStats((current) =>
            current ? overlaySponsorLiveFields(current, nextStats) : current,
          );
          setError(null);
          return true;
        }
        setStats(nextStats);
        setAppliedFilters({ dateFrom: nextDateFrom, dateTo: nextDateTo });
        setError(null);
        return true;
      } catch {
        if (requestId === statsRequestId.current && !silent) {
          setError("не удалось загрузить статистику");
          setStats(null);
        }
        return false;
      }
    })();
    statsRequestInFlight.current = request;

    try {
      return await request;
    } finally {
      if (statsRequestInFlight.current === request) {
        statsRequestInFlight.current = null;
      }
      if (requestId === statsRequestId.current) {
        if (showLoading) setLoading(false);
      }
    }
  }

  useEffect(() => {
    const defaults = defaultDateRange();
    setDateFrom(defaults.dateFrom);
    setDateTo(defaults.dateTo);
    setAppliedFilters(defaults);
    void load(defaults.dateFrom, defaults.dateTo, { showLoading: true });
  }, [resourceId]);

  useEffect(() => {
    let active = true;
    let refreshTimer: number | undefined;

    function scheduleRefresh() {
      if (!active || document.hidden || refreshTimer !== undefined) return;
      refreshTimer = window.setTimeout(() => {
        refreshTimer = undefined;
        void refreshAndSchedule();
      }, STATS_REFRESH_MS);
    }

    async function waitForCurrentRequest() {
      const request = statsRequestInFlight.current;
      if (request) await request;
    }

    async function refreshAndSchedule() {
      if (!active || document.hidden) return;
      await load(appliedFilters.dateFrom, appliedFilters.dateTo, {
        showLoading: false,
        silent: true,
      });
      await waitForCurrentRequest();
      scheduleRefresh();
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
        refreshTimer = undefined;
        return;
      }
      if (refreshTimer === undefined) void refreshAndSchedule();
    }

    void waitForCurrentRequest().then(scheduleRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      active = false;
      if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [resourceId, appliedFilters.dateFrom, appliedFilters.dateTo]);

  useEffect(() => {
    if (!loading && restoreStatsHeadingFocus.current) {
      statsHeadingRef.current?.focus();
      restoreStatsHeadingFocus.current = false;
    }
  }, [loading, stats]);

  async function exportCsv() {
    setExporting(true);
    setError(null);
    setMessage(null);
    try {
      const r = await sponsorApi.sendStatsExport({
        dateFrom: appliedFilters.dateFrom,
        dateTo: appliedFilters.dateTo,
        mode: "events",
        resourceId,
      });
      if (r.error || !r.data) {
        setError("не удалось отправить выгрузку в бот");
        return;
      }
      setMessage("файл успешно отправлен в бота!");
    } catch {
      setError("не удалось отправить файл в бот");
    } finally {
      setExporting(false);
    }
  }

  if (loading || (!stats && !error)) {
    return (
      <SponsorStatsLoadingScreen
        cardRows={SPONSOR_STATS_LOADING_ROWS}
        focusHeading={restoreStatsHeadingFocus.current}
        headingRef={statsHeadingRef}
        onBack={onBack}
      />
    );
  }

  return (
    <div className="tp-screen tp-driver-stats-screen">
      <div
        className={onBack ? "tp-sponsor-stats-header" : "tp-driver-stats-hero"}
      >
        {onBack ? (
          <button
            className="tp-sponsor-stats-back"
            onClick={onBack}
            type="button"
            aria-label="назад"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : null}
        <h1
          ref={statsHeadingRef}
          className="tp-driver-stats-title"
          tabIndex={-1}
        >
          {resourceId
            ? resourceName || "статистика ресурса"
            : "общая статистика"}
        </h1>
      </div>

      <DateFilters
        dateFrom={dateFrom}
        dateTo={dateTo}
        loading={loading}
        onDateFrom={setDateFrom}
        onDateTo={setDateTo}
        onApply={() =>
          load(dateFrom, dateTo, { focusHeading: true, refresh: true })
        }
        onReset={() => {
          const defaults = defaultDateRange();
          setDateFrom(defaults.dateFrom);
          setDateTo(defaults.dateTo);
          void load(defaults.dateFrom, defaults.dateTo, {
            focusHeading: true,
            refresh: true,
          });
        }}
      />

      {error ? <div className="tp-alert">{error}</div> : null}
      {message ? (
        <div className="tp-card-soft tp-success-message text-sm">{message}</div>
      ) : null}

      {stats ? (
        <>
          <ResourceSubscribersCard resources={stats.resources} />

          <button
            className="tp-button-primary tp-stats-wide-button w-full"
            disabled={exporting}
            onClick={exportCsv}
          >
            <Download
              className="h-[1.125rem] w-[1.125rem]"
              aria-hidden="true"
            />
            <span>{exporting ? "отправляем..." : "получить выгрузку"}</span>
          </button>
        </>
      ) : null}
    </div>
  );
}

function SponsorStatsLoadingScreen({
  cardRows,
  focusHeading = false,
  headingRef,
  onBack,
}: {
  cardRows: readonly number[];
  focusHeading?: boolean;
  headingRef: RefObject<HTMLHeadingElement>;
  onBack?: () => void;
}) {
  useEffect(() => {
    if (focusHeading) headingRef.current?.focus();
  }, [focusHeading, headingRef]);

  return (
    <div className="tp-screen tp-driver-stats-screen tp-stats-loading-screen">
      <div
        className={onBack ? "tp-sponsor-stats-header" : "tp-driver-stats-hero"}
      >
        {onBack ? (
          <button
            className="tp-sponsor-stats-back"
            onClick={onBack}
            type="button"
            aria-label="назад"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : null}
        <h1
          ref={headingRef}
          className="tp-driver-stats-title tp-stats-loading-title"
          tabIndex={focusHeading ? -1 : undefined}
        >
          загружаем статистику
        </h1>
      </div>

      <StatsLoadingCards cardRows={cardRows} />
    </div>
  );
}

function DateFilters({
  dateFrom,
  dateTo,
  loading,
  onDateFrom,
  onDateTo,
  onApply,
  onReset,
}: {
  dateFrom: string;
  dateTo: string;
  loading: boolean;
  onDateFrom: (value: string) => void;
  onDateTo: (value: string) => void;
  onApply: () => void;
  onReset: () => void;
}) {
  return (
    <div className="tp-stats-filter-panel">
      <div className="tp-stats-date-row">
        <DateField
          value={dateFrom}
          ariaLabel="дата начала"
          onChange={onDateFrom}
          icon={<CalendarHeart className="h-5 w-5" />}
        />
        <DateField
          value={dateTo}
          ariaLabel="дата окончания"
          onChange={onDateTo}
          icon={<CalendarHeart className="h-5 w-5" />}
        />
      </div>
      <div className="tp-stats-action-row">
        <button
          className="tp-stats-filter-button"
          disabled={loading}
          onClick={onApply}
        >
          <RefreshCw className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
          <span>обновить</span>
        </button>
        <button
          className="tp-stats-filter-button"
          disabled={loading}
          onClick={onReset}
        >
          <Trash2 className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
          <span>сбросить</span>
        </button>
      </div>
    </div>
  );
}

function ResourceSubscribersCard({
  resources,
}: {
  resources: SponsorResource[];
}) {
  return (
    <section
      className="tp-stats-card"
      aria-label="зачтённые действия по ресурсам"
    >
      <h2 className="tp-stats-section-title">результаты по ресурсам</h2>
      {resources.length === 0 ? (
        <StatsRow label="ресурсов нет" value="0" />
      ) : (
        resources.map((resource) =>
          resource.type === "bot" ? (
            <Fragment key={resource.id}>
              <StatsRow
                label={`${resource.name} — подтверждено`}
                value={String(resource.verified_starts)}
              />
              <StatsRow
                label={`${resource.name} — зачтено`}
                value={String(resource.conversions)}
              />
              {resource.verified_unbilled_starts > 0 ? (
                <StatsRow
                  label={`${resource.name} — подтверждено, не зачтено`}
                  value={String(resource.verified_unbilled_starts)}
                />
              ) : null}
            </Fragment>
          ) : (
            <StatsRow
              key={resource.id}
              label={resource.name}
              value={String(resource.subscribers)}
            />
          ),
        )
      )}
    </section>
  );
}

function StatsRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="tp-stats-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function todayIso(): string {
  return moscowDateIso(new Date());
}

function moscowDateIso(value: Date): string {
  const parts = new Intl.DateTimeFormat("ru-RU", {
    timeZone: "Europe/Moscow",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function defaultDateRange(): {
  dateFrom: string;
  dateTo: string;
} {
  const today = todayIso();
  return {
    dateFrom: today,
    dateTo: today,
  };
}

function acceptSnapshot(
  latestByKey: Map<string, number>,
  key: string,
  generatedAt: string | undefined,
): boolean {
  const candidate = generatedAt ? Date.parse(generatedAt) : Number.NaN;
  const latest = latestByKey.get(key);
  if (
    latest !== undefined &&
    (!Number.isFinite(candidate) || candidate < latest)
  ) {
    return false;
  }
  if (Number.isFinite(candidate)) {
    latestByKey.set(key, Math.max(latest ?? candidate, candidate));
  }
  return true;
}

export function overlaySponsorLiveFields(analytics: Stats, live: Stats): Stats {
  return {
    ...analytics,
    balance: live.balance,
    topups: live.topups,
    spends: live.spends,
  };
}
